import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { BRAND } from '@theme/brand.ts';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type TouchEvent,
} from 'react';

import {
  freezeCard,
  getBotInfo,
  getCardPan,
  getCardShipment,
  getCardTransactions,
  listCards,
  unfreezeCard,
  type CardShipment,
  type CardSummary,
  type CardTransaction,
} from '../api/client.ts';
import { STEP_UP_ACTIONS } from '../api/step-up.ts';
import { CardFace } from '../components/card.tsx';
import {
  IconArrowDown,
  IconCheck,
  IconClock,
  IconList,
  IconSnowflake,
  IconSupport,
} from '../components/icons.tsx';
import { BottomSheet } from '../components/sheet.tsx';
import {
  CardsDashboardSkeleton,
  EmptyState,
  ErrorState,
  TransactionListSkeleton,
} from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { Topbar } from '../components/topbar.tsx';
import { AUTO_ACTIVATION_COPY, formatRateLimited } from '../lib/auto-activation-copy.ts';
import { useAutoActivationOrchestrator } from '../lib/auto-activation.ts';
import { cardsRefetchInterval, useOrchestratorPhases } from '../lib/refetch-interval.ts';
import {
  currencySymbol,
  formatMagnitude,
  isCredit,
  movedMoney,
  readBilling,
  readFx,
  readMerchant,
  statusNote,
  txTypeLabel,
} from '../lib/transaction-view.ts';
import { isStepUpCancelled, useStepUpGate } from '../lib/use-step-up.tsx';
import { usePlatform } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

// The home screen shows a teaser; the full history lives behind "View all".
const HOME_RECENT_TX_COUNT = 5;

const splitAmount = (raw: string | undefined): { readonly int: string; readonly dec: string } => {
  if (raw === undefined) return { int: '—', dec: '' };
  const n = Number(raw);
  if (Number.isNaN(n)) return { int: raw, dec: '' };
  const abs = Math.abs(n);
  const int = Math.floor(abs).toLocaleString('en-US');
  const decPart = abs.toFixed(2).split('.')[1];
  return { int, dec: `.${decPart ?? '00'}` };
};

const statusMeta = (raw: string | undefined): { readonly cls: string; readonly label: string } => {
  switch ((raw ?? 'ACTIVE').toUpperCase()) {
    case 'FROZEN':
      return { cls: 'frozen', label: 'Frozen' };
    case 'BLOCKED':
      return { cls: 'blocked', label: 'Blocked' };
    case 'EXPIRED':
      return { cls: 'blocked', label: 'Expired' };
    case 'DELETED':
      return { cls: 'blocked', label: 'Closed' };
    case 'PENDING':
      return { cls: 'pending', label: 'Pending' };
    default:
      return { cls: 'active', label: 'Active' };
  }
};

const formatTxTime = (iso: string | undefined): string => {
  if (iso === undefined || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const isActivePhysical = (card: CardSummary): boolean =>
  (card.status ?? '').toUpperCase() === 'ACTIVE' && (card.type ?? '').toUpperCase() === 'PHYSICAL';

// A delivered physical card whose chip has not been activated yet. The REAP
// card status is already ACTIVE (usable online from creation), so chip
// activation is signalled only by physicalCardStatus / physicalActivatedAt.
// This is the one card state that shows the Activate-card button.
const isDeliveredUnactivated = (card: CardSummary): boolean =>
  (card.type ?? '').toUpperCase() === 'PHYSICAL' &&
  card.physicalCardStatus === 'DELIVERED' &&
  (card.physicalActivatedAt === null || card.physicalActivatedAt === undefined);

const NEEDS_SHIPPING_STATUSES = new Set(['NOT_ORDERED', 'SHIPMENT_FAILED', 'SHIPMENT_CANCELLED']);

const needsShipping = (card: CardSummary): boolean =>
  card.physicalCardStatus !== undefined && NEEDS_SHIPPING_STATUSES.has(card.physicalCardStatus);

const TRACKED_STATUSES = new Set([
  'IN_PRODUCTION',
  'SENT_TO_USER',
  'SHIPMENT_FAILED',
  'SHIPMENT_CANCELLED',
]);

const hasShipmentTracking = (card: CardSummary): boolean =>
  card.physicalCardStatus !== undefined && TRACKED_STATUSES.has(card.physicalCardStatus);

interface ShipmentStepState {
  readonly index: number;
  readonly failed: boolean;
  readonly cancelled: boolean;
}

const STATUS_TO_STEP: Record<string, ShipmentStepState> = {
  CARD_SHIPPING_REQUESTED: { index: 1, failed: false, cancelled: false },
  CARD_SHIPPING_INFO_AVAILABLE: { index: 2, failed: false, cancelled: false },
  CARD_IN_TRANSIT: { index: 3, failed: false, cancelled: false },
  CARD_OUT_FOR_DELIVERY: { index: 4, failed: false, cancelled: false },
  CARD_DELIVERED: { index: 5, failed: false, cancelled: false },
  CARD_SHIPPING_ATTEMPT_FAILED: { index: 4, failed: true, cancelled: false },
  CARD_SHIPPING_EXCEPTION: { index: 2, failed: true, cancelled: false },
  CARD_SHIPPING_CANCELED: { index: -1, failed: false, cancelled: true },
};

const getShipmentStep = (
  physicalCardStatus: string,
  shipmentStatus?: string | null,
): ShipmentStepState => {
  if (shipmentStatus != null) {
    return STATUS_TO_STEP[shipmentStatus] ?? { index: 0, failed: false, cancelled: false };
  }
  switch (physicalCardStatus) {
    case 'IN_PRODUCTION':
      return { index: 1, failed: false, cancelled: false };
    case 'SENT_TO_USER':
      return { index: 2, failed: false, cancelled: false };
    case 'DELIVERED':
    case 'ACTIVATED':
      return { index: 5, failed: false, cancelled: false };
    case 'SHIPMENT_FAILED':
      return { index: 4, failed: true, cancelled: false };
    case 'SHIPMENT_CANCELLED':
      return { index: -1, failed: false, cancelled: true };
    default:
      return { index: 1, failed: false, cancelled: false };
  }
};

const SHIPMENT_STEPS = [
  'Ordered',
  'In production',
  'Dispatched',
  'In transit',
  'Out for delivery',
  'Delivered',
];

const isSafeTrackingUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' && /^https:\/\//i.test(url);

export const ShipmentTracker = ({
  status,
  shipment,
  busy = false,
}: {
  readonly status: string;
  readonly shipment?: CardShipment | null;
  readonly busy?: boolean;
}): ReactNode => {
  const platform = usePlatform();
  const { index: stepIndex, failed, cancelled } = getShipmentStep(status, shipment?.status);
  const [liveStatus, setLiveStatus] = useState('');
  useEffect(() => {
    if (cancelled) return;
    const label = SHIPMENT_STEPS[stepIndex] ?? 'updating';
    // Announce a tick after mount: a polite live region inserted with its text
    // already present is not read out, so the status would be silent on open.
    const id = window.setTimeout(() => {
      setLiveStatus(`Delivery status: ${label}${failed ? ', last attempt failed' : ''}`);
    }, 60);
    return () => {
      window.clearTimeout(id);
    };
  }, [stepIndex, failed, cancelled]);
  if (cancelled) {
    return (
      <div className="shipment-tracker">
        <p className="shipment-cancelled">Shipping was cancelled.</p>
      </div>
    );
  }
  const safeTrackingUrl = isSafeTrackingUrl(shipment?.trackingUrl) ? shipment.trackingUrl : null;
  const hasTrackingDetails = shipment?.trackingNumber != null || safeTrackingUrl != null;
  return (
    <div className="shipment-tracker">
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveStatus}
      </p>
      {failed ? (
        <p className="shipment-warning">
          Delivery attempt failed. Contact support to re-arrange shipping.
        </p>
      ) : null}
      <ol className="stepper">
        {SHIPMENT_STEPS.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          const cls = done ? 'done' : active ? 'active' : 'pending';
          const busyCls = active && busy ? ' busy' : '';
          // The terminal step gets the success treatment (credit-green dot
          // and rail) instead of the in-progress accent: arriving is an
          // outcome, not a stage.
          const deliveredCls = active && i === SHIPMENT_STEPS.length - 1 ? ' delivered' : '';
          return (
            <li
              key={label}
              className={`stepper-step ${cls}${busyCls}${deliveredCls}`}
              aria-current={active ? 'step' : undefined}
            >
              <div className="stepper-dot" aria-hidden="true">
                {done ? <IconCheck size={11} /> : active ? <IconClock size={11} /> : null}
              </div>
              <span className="stepper-label">{label}</span>
              <span className="sr-only">
                {done ? ' — completed' : active ? ' — in progress' : ' — pending'}
              </span>
            </li>
          );
        })}
      </ol>
      {shipment != null ? (
        <div className="tracking-details">
          {shipment.courierName != null ? (
            <div className="tracking-row">
              <span>Courier</span>
              <strong>{shipment.courierName}</strong>
            </div>
          ) : null}
          {hasTrackingDetails ? (
            <>
              {shipment.trackingNumber != null ? (
                <div className="tracking-row">
                  <span>Tracking number</span>
                  <strong>{shipment.trackingNumber}</strong>
                </div>
              ) : null}
              {safeTrackingUrl != null ? (
                <a
                  href={safeTrackingUrl}
                  rel="noreferrer"
                  className="tracking-link"
                  onClick={(e): void => {
                    // Open in the device browser via Telegram's openLink rather
                    // than navigating the WebView, which would trap the courier
                    // page inside the mini-app.
                    e.preventDefault();
                    platform.openLink(safeTrackingUrl);
                  }}
                >
                  Track package <span aria-hidden="true">↗</span>
                </a>
              ) : null}
            </>
          ) : (
            <p className="tracking-unavailable">
              Tracking details will appear once the courier provides them.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Phase-specific UI surface for the auto-activation handshake. Lives
// inside the delivery-status bottom sheet so the stepper and the activity
// indicator render next to each other. The cardholder never sees, types,
// or stores the 6-digit code; this panel only renders phase + error copy.
export const AutoActivationPanel = ({
  state,
}: {
  readonly state: ReturnType<typeof useAutoActivationOrchestrator>;
}): ReactNode => {
  const { phase, errorState, retryAfterSec, canRetry, retry } = state;
  // Render-time countdown so the wait CTA shows seconds-remaining instead
  // of a static label. Re-ticks at 1Hz while waiting_retry is active.
  const [now, setNow] = useState<number>(Date.now());
  const failedAtRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (phase === 'waiting_retry' && failedAtRef.current === undefined) {
      failedAtRef.current = Date.now();
    }
    if (phase !== 'waiting_retry') {
      failedAtRef.current = undefined;
    }
  }, [phase]);
  useEffect(() => {
    if (phase !== 'waiting_retry') return;
    const handle = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return (): void => {
      window.clearInterval(handle);
    };
  }, [phase]);

  if (phase === 'idle' || phase === 'succeeded') return null;

  const ariaText =
    phase === 'working'
      ? AUTO_ACTIVATION_COPY.aria_phase_working
      : phase === 'failed' || phase === 'retry_armed'
        ? AUTO_ACTIVATION_COPY.aria_phase_failed
        : AUTO_ACTIVATION_COPY.aria_phase_waiting;

  const remainingSec =
    phase === 'waiting_retry' && failedAtRef.current !== undefined && retryAfterSec !== undefined
      ? Math.max(0, Math.ceil((failedAtRef.current + retryAfterSec * 1000 - now) / 1000))
      : 0;

  let body: ReactNode;
  if (phase === 'working') {
    body = <p className="auto-activate-status">{AUTO_ACTIVATION_COPY.loading_short}</p>;
  } else if (phase === 'waiting_retry') {
    const copy =
      errorState === 'rate_limited'
        ? formatRateLimited(remainingSec)
        : errorState === 'retrieval_5xx'
          ? AUTO_ACTIVATION_COPY.err_retrieval_5xx
          : errorState === 'activate_uncertain'
            ? AUTO_ACTIVATION_COPY.err_uncertain
            : AUTO_ACTIVATION_COPY.err_retrieval_5xx;
    body = (
      <>
        <p className="auto-activate-status">{copy}</p>
        <button type="button" className="btn-ghost" disabled aria-disabled="true">
          {AUTO_ACTIVATION_COPY.cta_try_again}
        </button>
      </>
    );
  } else if (phase === 'retry_armed') {
    body = (
      <>
        <p className="auto-activate-status">
          {errorState === 'rate_limited'
            ? formatRateLimited(0)
            : AUTO_ACTIVATION_COPY.err_retrieval_5xx}
        </p>
        <button type="button" className="btn-ghost" onClick={retry} disabled={!canRetry}>
          {AUTO_ACTIVATION_COPY.cta_try_again}
        </button>
      </>
    );
  } else {
    // terminal failure
    const copy =
      errorState === 'retrieval_4xx'
        ? AUTO_ACTIVATION_COPY.err_retrieval_4xx
        : errorState === 'invalid_code_panic'
          ? AUTO_ACTIVATION_COPY.err_invalid_code
          : AUTO_ACTIVATION_COPY.err_retrieval_4xx;
    body = (
      <>
        <p className="auto-activate-status">{copy}</p>
        <button type="button" className="btn-ghost">
          {AUTO_ACTIVATION_COPY.cta_contact_support}
        </button>
      </>
    );
  }

  return (
    <div className="auto-activate-panel">
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {ariaText}
      </p>
      {body}
    </div>
  );
};

// Owns the per-card auto-activation flow. Rendered with key={card.id} so each
// delivered, unactivated physical card gets its own orchestrator instance —
// the hook's one-shot start() guard and phase state are per-mount, so keying
// by id keeps two such cards isolated when the carousel swaps between them
// (without the key, the first card's non-idle phase would suppress the
// second card's Activate button and its start() would stay blocked).
const ActivateCardCTA = ({ card }: { readonly card: CardSummary }): ReactNode => {
  const autoActivate = useAutoActivationOrchestrator(card);
  // One-shot success toast when the server-side handshake completes.
  const prevPhaseRef = useRef<string>('idle');
  useEffect(() => {
    if (autoActivate.phase === 'succeeded' && prevPhaseRef.current !== 'succeeded') {
      showToast(AUTO_ACTIVATION_COPY.success_toast);
    }
    prevPhaseRef.current = autoActivate.phase;
  }, [autoActivate.phase]);
  return (
    // Sits below the action row and grows in via a grid-rows reveal, so
    // appearing on swipe never shifts the Top up / Freeze / Details row above.
    <div className="activate-reveal">
      <div className="activate-reveal-inner">
        {autoActivate.phase === 'idle' ? (
          // Server-side activation: the tap fires the bot-proxy handshake; the
          // 6-digit code never reaches this app. Replaced by the progress/error
          // panel once tapped, and removed once the card reports activated.
          <div className="action-row activate-row">
            <button
              type="button"
              className="btn-ghost"
              onClick={(): void => {
                autoActivate.start();
              }}
            >
              {AUTO_ACTIVATION_COPY.cta_activate}
            </button>
          </div>
        ) : (
          <AutoActivationPanel state={autoActivate} />
        )}
      </div>
    </div>
  );
};

const TxRow = ({ tx }: { readonly tx: CardTransaction }): ReactNode => {
  const status = statusNote(tx);
  // Nothing moved: the figure shown is what was presented, not what was taken.
  // The teaser deliberately carries only the status word, not the provider's
  // full decline text — that belongs on the Activity screen, which has the room.
  const settled = movedMoney(tx);
  const credit = isCredit(tx);
  const billing = readBilling(tx);
  const fx = readFx(tx);
  const fxParts = fx !== null ? formatMagnitude(fx.amount) : null;
  const cls = !settled
    ? // A struck-through dash reads as a typo, so the two unsettled shapes get
      // their own class: a real figure is crossed out, an absent one is just dim.
      billing !== null
      ? 'unsettled'
      : 'absent'
    : status === 'Pending'
      ? 'pending'
      : credit
        ? 'credit'
        : 'debit';
  const sign = credit ? '+' : '−';
  // No card-currency figure (a declined foreign-currency attempt, never
  // priced): the row shows a dash rather than a dollar amount it cannot
  // stand behind. What was presented is on the merchant line below.
  const sym = billing !== null ? currencySymbol(billing.currency) : '';
  const parts = billing !== null ? formatMagnitude(billing.amount) : null;
  const desc = readMerchant(tx);
  return (
    <div className="tx-row">
      <div className="tx-left">
        <p className="tx-desc">{desc}</p>
        <p className="tx-meta">
          {formatTxTime(tx.createdAt)} · {txTypeLabel(tx, credit)}
          {status !== null ? ` · ${status}` : ''}
        </p>
      </div>
      <div className="tx-right">
        <p className={`tx-amount ${cls}`}>
          {parts !== null ? `${sign}${sym}${parts.int}${parts.dec}` : '—'}
        </p>
        {fx !== null && fxParts !== null ? (
          <p className={`tx-amount-sub${settled ? '' : ' unsettled'}`}>
            {currencySymbol(fx.currency)}
            {fxParts.int}
            {fxParts.dec}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const CardsPage = (): ReactNode => {
  const platform = usePlatform();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ from: cardsRoute.id });
  const [index, setIndex] = useState<number>(0);
  const [swapDir, setSwapDir] = useState<'left' | 'right' | null>(null);
  const [animKey, setAnimKey] = useState<number>(0);
  const hasSwappedRef = useRef<boolean>(false);
  const hasJumpedToCardRef = useRef<boolean>(false);
  const [panOpen, setPanOpen] = useState<boolean>(false);
  const [panUrl, setPanUrl] = useState<string>('');
  const [panError, setPanError] = useState<string>('');
  const [panPending, setPanPending] = useState<boolean>(false);
  const stepUp = useStepUpGate();
  const [trackOpen, setTrackOpen] = useState<boolean>(false);
  const swipeRef = useRef<{ startX: number; dx: number; active: boolean }>({
    startX: 0,
    dx: 0,
    active: false,
  });

  const phases = useOrchestratorPhases();
  const cardsQ = useQuery({
    queryKey: ['cards'],
    queryFn: ({ signal }) => listCards(signal),
    refetchInterval: (q): number | false => cardsRefetchInterval(q.state.data?.cards, phases),
  });
  const cards = cardsQ.data?.cards ?? [];
  // Wallet feature flag, read once per deploy. Defaults closed so the Vault
  // entry point stays hidden until bot-info confirms the feature is on.
  const botInfoQ = useQuery({
    queryKey: ['bot-info'],
    queryFn: ({ signal }) => getBotInfo(signal),
    staleTime: Infinity,
  });
  const walletEnabled = botInfoQ.data?.walletEnabled ?? false;
  // Jump the carousel to ?cardId once — the first time the matching card
  // becomes available. The ref prevents re-triggering on every user swipe.
  useEffect(() => {
    if (search.cardId === undefined || hasJumpedToCardRef.current) return;
    const i = cards.findIndex((c) => c.id === search.cardId);
    if (i < 0) return;
    hasJumpedToCardRef.current = true;
    if (i !== index) setIndex(i);
  }, [cards, search.cardId, index]);
  const current = cards[index];
  const shipmentQ = useQuery({
    queryKey: ['shipment', current?.id, trackOpen],
    queryFn: ({ signal }) =>
      current !== undefined ? getCardShipment(current.id, signal) : Promise.resolve(null),
    enabled: trackOpen && current !== undefined && hasShipmentTracking(current),
  });

  // Animates a carousel jump: dir = 'left' means the new card slides in from
  // the right (user swiped left). null = no animation (programmatic jump,
  // e.g. ?cardId preselect).
  const goTo = useCallback(
    (next: number, dir: 'left' | 'right' | null): void => {
      if (next === index || next < 0 || next >= cards.length) return;
      hasSwappedRef.current = true;
      setSwapDir(dir);
      setAnimKey((k) => k + 1);
      setIndex(next);
    },
    [index, cards.length],
  );

  const txQ = useQuery({
    queryKey: ['txns', current?.id ?? '__none__', 'recent'],
    queryFn: ({ signal }) =>
      current !== undefined
        ? getCardTransactions(current.id, 1, HOME_RECENT_TX_COUNT, signal)
        : Promise.resolve({ data: [], page: 1, limit: HOME_RECENT_TX_COUNT }),
    enabled: current !== undefined,
    // Recent activity rarely changes second-to-second; keep each card's list
    // fresh for 30s so swiping back and forth between cards reuses the cache
    // instead of refetching on every swap.
    staleTime: 30_000,
  });

  const freezeM = useMutation({
    mutationFn: async ({ id, frozen }: { id: string; frozen: boolean }) => {
      if (frozen) await unfreezeCard(id);
      else await freezeCard(id);
    },
    onSuccess: async (_d, vars): Promise<void> => {
      await qc.invalidateQueries({ queryKey: ['cards'] });
      showToast(vars.frozen ? 'Card unfrozen' : 'Card frozen');
    },
    onError: (e: unknown) => {
      showToast(e instanceof Error ? e.message : 'Action failed');
    },
  });

  const onSwipeStart = (e: TouchEvent<HTMLDivElement>): void => {
    if (cards.length < 2) return;
    const t = e.touches[0];
    if (t === undefined) return;
    swipeRef.current = { startX: t.clientX, dx: 0, active: true };
  };
  const onSwipeMove = (e: TouchEvent<HTMLDivElement>): void => {
    if (!swipeRef.current.active) return;
    const t = e.touches[0];
    if (t === undefined) return;
    swipeRef.current.dx = t.clientX - swipeRef.current.startX;
  };
  const onSwipeEnd = (): void => {
    if (!swipeRef.current.active) return;
    const { dx } = swipeRef.current;
    swipeRef.current.active = false;
    if (Math.abs(dx) > 50) {
      if (dx < 0 && index < cards.length - 1) goTo(index + 1, 'left');
      if (dx > 0 && index > 0) goTo(index - 1, 'right');
    }
  };

  // Full card details are behind a second factor. The confirmation runs
  // before the details sheet opens, so the two never stack on screen.
  const onOpenPan = async (): Promise<void> => {
    if (current === undefined) return;
    const card = current;
    setPanError('');
    setPanUrl('');
    setPanPending(true);
    try {
      const { accessUrl } = await stepUp.guard(
        {
          action: STEP_UP_ACTIONS.CARD_DETAILS_REVEAL,
          params: { cardId: card.id },
          summary: `Show the full number, expiry and CVV for card •••• ${card.lastFour ?? '????'}.`,
          onSetUpBiometrics: (): void => {
            void navigate({ to: '/security' });
          },
        },
        (grant) => getCardPan(card.id, grant),
      );
      setPanUrl(accessUrl);
      setPanOpen(true);
    } catch (e: unknown) {
      if (isStepUpCancelled(e)) return;
      setPanError(e instanceof Error ? e.message : 'Could not load card details.');
      setPanOpen(true);
    } finally {
      setPanPending(false);
    }
  };

  const onClosePan = (): void => {
    setPanOpen(false);
    setPanUrl('');
    setPanError('');
  };

  const onToggleFreeze = (): void => {
    if (current === undefined) return;
    const status = (current.status ?? '').toUpperCase();
    if (status !== 'ACTIVE' && status !== 'FROZEN') return;
    const frozen = status === 'FROZEN';
    const last4 = current.lastFour ?? '????';
    const msg = frozen
      ? `Unfreeze card •••• ${last4}?`
      : `Freeze card •••• ${last4}? Payments will be paused until you unfreeze.`;
    platform.confirm(msg, (ok) => {
      if (!ok) return;
      freezeM.mutate({ id: current.id, frozen });
    });
  };

  if (cardsQ.isLoading)
    return (
      <main className="screen">
        <Topbar />
        <CardsDashboardSkeleton />
      </main>
    );
  if (cardsQ.isError) {
    return (
      <ErrorState
        message={cardsQ.error instanceof Error ? cardsQ.error.message : 'Could not load cards'}
        onRetry={(): void => {
          void cardsQ.refetch();
        }}
      />
    );
  }
  if (cards.length === 0 || current === undefined) {
    return (
      <main className="screen">
        <Topbar />
        <EmptyState message="No cards yet." />
        <div className="empty-cta">
          <button
            type="button"
            className="btn-pill"
            onClick={(): void => {
              void navigate({ to: '/new-card' });
            }}
          >
            + New card
          </button>
        </div>
      </main>
    );
  }

  const status = statusMeta(current.status);
  const frozen = status.cls === 'frozen';
  const balance = splitAmount(current.balance);
  const symbol = currencySymbol(current.currency);
  const freezeDisabled = status.cls !== 'active' && status.cls !== 'frozen';

  return (
    <main className="screen">
      <Topbar />

      <div
        role="group"
        aria-roledescription="carousel"
        aria-label={`Card ${String(index + 1)} of ${String(cards.length)}`}
        tabIndex={cards.length > 1 ? 0 : -1}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>): void => {
          if (cards.length < 2) return;
          if (e.key === 'ArrowRight' && index < cards.length - 1) {
            e.preventDefault();
            goTo(index + 1, 'left');
          } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            goTo(index - 1, 'right');
          }
        }}
        onTouchStart={onSwipeStart}
        onTouchMove={onSwipeMove}
        onTouchEnd={onSwipeEnd}
      >
        <div
          key={animKey}
          className={`card-stage${swapDir !== null ? ` swap-${swapDir}` : ''}`}
          onAnimationEnd={(): void => {
            setSwapDir(null);
          }}
        >
          <CardFace card={current} noFadeIn={hasSwappedRef.current || swapDir !== null} />
        </div>
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          Card {String(index + 1)} of {String(cards.length)}, ending {current.lastFour ?? 'unknown'}
        </p>
      </div>

      <div className="carousel-meta fade-in d1">
        <div className="carousel-meta-row">
          {cards.length > 1 ? (
            <div className="carousel-dots" aria-hidden="true">
              {cards.map((c, i) => (
                <span key={c.id} className={`carousel-dot${i === index ? ' active' : ''}`} />
              ))}
            </div>
          ) : null}
          <div className="carousel-meta-right">
            <p className="status-line">
              <span className={`status-dot ${status.cls}`} />
              {status.label}
            </p>
            <button
              type="button"
              className="btn-pill"
              onClick={(): void => {
                void navigate({ to: '/new-card' });
              }}
            >
              + New card
            </button>
          </div>
        </div>
      </div>

      <div className="hero fade-in d2">
        <p className="eyebrow hero-eyebrow">Available balance</p>
        <div className="hero-amount" aria-live="polite">
          <span className="hero-symbol">{symbol}</span>
          <span className="hero-int">{balance.int}</span>
          <span className="hero-dec">{balance.dec || '.00'}</span>
        </div>
        <p className="hero-meta">{current.currency ?? 'USD'}</p>
      </div>

      <div className="action-row fade-in d3">
        <button
          type="button"
          className="btn-ghost"
          onClick={(): void => {
            void navigate({ to: '/topup', search: { cardId: current.id } });
          }}
          disabled={frozen}
        >
          <IconArrowDown /> Top up
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={onToggleFreeze}
          disabled={freezeDisabled || freezeM.isPending}
        >
          <IconSnowflake /> {frozen ? 'Unfreeze' : 'Freeze'}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={panPending}
          onClick={(): void => {
            void onOpenPan();
          }}
        >
          <IconList /> Details
        </button>
        {isActivePhysical(current) ? (
          <button
            type="button"
            className="btn-ghost"
            onClick={(): void => {
              void navigate({ to: '/change-pin', search: { cardId: current.id } });
            }}
          >
            Change PIN
          </button>
        ) : null}
      </div>

      {walletEnabled ? (
        <div className="action-row-secondary fade-in d3">
          <button
            type="button"
            className="btn-ghost"
            onClick={(): void => {
              void navigate({ to: '/vault' });
            }}
          >
            Vault
          </button>
        </div>
      ) : null}

      {BRAND.supportAgent ? (
        <div className="action-row-secondary fade-in d3">
          <button
            type="button"
            className="btn-ghost"
            onClick={(): void => {
              void navigate({ to: '/support' });
            }}
          >
            <IconSupport size={14} /> Support
          </button>
        </div>
      ) : null}

      {isDeliveredUnactivated(current) ? <ActivateCardCTA key={current.id} card={current} /> : null}

      {needsShipping(current) || hasShipmentTracking(current) ? (
        <div className="action-row-secondary fade-in d3">
          {needsShipping(current) ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={(): void => {
                void navigate({ to: '/ship', search: { cardId: current.id } });
              }}
            >
              Ship card
            </button>
          ) : null}
          {hasShipmentTracking(current) ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={(): void => {
                setTrackOpen(true);
              }}
            >
              Track delivery
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="section-header fade-in d4">
        <p className="eyebrow">Recent activity</p>
        <button
          type="button"
          className="section-link"
          onClick={(): void => {
            void navigate({ to: '/transactions', search: { cardId: current.id } });
          }}
        >
          View all
        </button>
      </div>
      <div className="hairline fade-in d4" style={{ marginTop: 8 }} />

      <div className="fade-in d4">
        {txQ.isLoading ? (
          <TransactionListSkeleton rows={3} />
        ) : txQ.isError ? (
          <ErrorState
            message={txQ.error instanceof Error ? txQ.error.message : 'Could not load activity'}
            onRetry={(): void => {
              void txQ.refetch();
            }}
          />
        ) : (txQ.data?.data ?? []).length === 0 ? (
          <div className="empty-state">No recent activity</div>
        ) : (
          (txQ.data?.data ?? []).slice(0, HOME_RECENT_TX_COUNT).map((tx, i, arr) => (
            <div key={tx.id}>
              <TxRow tx={tx} />
              {i < arr.length - 1 ? <div className="hairline" /> : null}
            </div>
          ))
        )}
      </div>

      <BottomSheet
        open={trackOpen}
        title="Delivery status"
        onClose={(): void => {
          setTrackOpen(false);
        }}
      >
        {current.physicalCardStatus !== undefined ? (
          <ShipmentTracker
            status={current.physicalCardStatus}
            {...(shipmentQ.data !== undefined ? { shipment: shipmentQ.data } : {})}
          />
        ) : null}
      </BottomSheet>

      <BottomSheet
        open={panOpen}
        title="Card details"
        footer="Single-use · Discarded on close"
        onClose={onClosePan}
      >
        {panError !== '' ? (
          <div className="sheet-error">{panError}</div>
        ) : panUrl === '' ? (
          <div className="sheet-spinner" aria-hidden="true" />
        ) : (
          <iframe
            className="sheet-iframe"
            src={panUrl}
            title="Card details"
            allow="clipboard-write"
          />
        )}
      </BottomSheet>

      {stepUp.sheet}
    </main>
  );
};

export interface CardsSearch {
  readonly cardId?: string;
}

export const cardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: CardsPage,
  validateSearch: (s: Record<string, unknown>): CardsSearch =>
    typeof s.cardId === 'string' ? { cardId: s.cardId } : {},
});
